import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TreeItem } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import { Typography } from "@material-ui/core";
import { Folder } from "@material-ui/icons";
import clsx from "clsx";
import { Directory } from "@interfaces/Directory";
import { useAsync } from "@hooks";
import { fileService } from "@services";

const useStyles = makeStyles((theme) => ({
    labelRoot: {
        display: "flex",
        alignItems: "center",
        padding: theme.spacing(0.5, 0),
    },
    labelIcon: {
        marginRight: theme.spacing(1),
    },
    labelText: {
        fontWeight: "inherit",
        flexGrow: 1,
    },
    labelSelected: {
        fontWeight: theme.typography.fontWeightBold,
    },
}));

type Props = {
    node: Directory;
    /** Предки этого узла от настоящего корня (не включая сам node) — нужны,
     * чтобы при клике отдать наверх ГОТОВЫЙ breadcrumb без сетевого запроса:
     * дерево и так уже построено из этих же данных, незачем заново поднимать
     * цепочку родителей через API на каждый клик (так уже сделано в экране
     * "Файловое хранилище" — там выбор вообще не ходит в сеть). */
    ancestors: Directory[];
    selectedId: number | null;
    autoOpenIds: Set<number>;
    /** Узел, который нужно подвести к верхней строке видимой области сразу
     * после монтирования (см. FilePickerDialog) — например модель, где лежит
     * уже выбранное фото, среди сотен других в том же бренде. */
    scrollTargetId: number | null;
    onSelect(chain: Directory[]): void;
};

const SelectableDirectoryTreeItemComponent = ({
    node,
    ancestors,
    selectedId,
    autoOpenIds,
    scrollTargetId,
    onSelect,
}: Props) => {
    const classes = useStyles();
    const itemRef = useRef<HTMLLIElement>(null);

    /** Стабильная ссылка, пока не меняются ancestors/node (у уже загруженной
     * статической структуры дерева они не меняются вообще) — важно для
     * мемоизации: пересоздавай эту ссылку на каждый рендер, и memo ниже по
     * дереву перестанет работать для всех детей разом. */
    const chain = useMemo(() => [...ancestors, node], [ancestors, node]);

    /** autoOpenIds — путь к изначально выбранной директории, вычисляется один раз
     * до монтирования дерева (см. FilePickerDialogContext), поэтому достаточно
     * начального значения стейта, без эффекта. */
    const [isOpened, setOpened] = useState(autoOpenIds.has(node.id));

    useEffect(() => {
        if (node.id === scrollTargetId) {
            itemRef.current?.scrollIntoView({ block: "start" });
        }
        // срабатывает один раз при монтировании этого элемента — повторный
        // скролл к той же цели делается через пересоздание дерева (key)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const hasOwnChilds = !!node.childs?.length;

    const { data } = useAsync(fileService.getDirectories, {
        canLoad: isOpened && !hasOwnChilds,
        setLoadingStatusOnUpdate: false,
        functionArgs: [{ parent_dir_id: node.id }],
    });

    const handleClick = useCallback(() => {
        setOpened(true);
        onSelect(chain);
    }, [chain, onSelect]);

    const fetchedItems = data?.result.items;
    const childs = hasOwnChilds ? node.childs : fetchedItems;

    /** Пока не знаем, есть ли у папки потомки (данные ещё не подгружены),
     * MUI TreeItem считает её листом и не рисует стрелку раскрытия — клик
     * только запускал бы подгрузку, а раскрыть можно было бы лишь вторым
     * кликом, когда дети уже известны. Рендерим заглушку, чтобы стрелка
     * была видна и кликабельна сразу, с первого раза. */
    const showPlaceholder = !hasOwnChilds && fetchedItems === undefined;

    return (
        <TreeItem
            ref={itemRef}
            onLabelClick={handleClick}
            onIconClick={handleClick}
            label={
                <div className={classes.labelRoot}>
                    <Folder color="action" className={classes.labelIcon} />
                    <Typography
                        variant="body2"
                        className={clsx(classes.labelText, {
                            [classes.labelSelected]: node.id === selectedId,
                        })}
                    >
                        {node.title}
                    </Typography>
                </div>
            }
            nodeId={node.id.toString()}
        >
            {showPlaceholder && <div />}
            {childs
                ?.sort((a, b) => a.title.localeCompare(b.title))
                .map((child) => (
                    <SelectableDirectoryTreeItem
                        key={child.id}
                        node={child}
                        ancestors={chain}
                        selectedId={selectedId}
                        autoOpenIds={autoOpenIds}
                        scrollTargetId={scrollTargetId}
                        onSelect={onSelect}
                    />
                ))}
        </TreeItem>
    );
};

/**
 * Дерево строится из уже загруженных 2 уровней поддерева под корнем —
 * это тысячи узлов (все модели всех брендов), и без мемоизации смена
 * selectedId (клик по любой папке) пере-рендерила их ВСЕ разом — именно
 * это и давало заметную задержку на каждый клик (кэш в directoryCache.ts
 * убрал лишние сетевые запросы, но не саму проблему рендера). Кастомный
 * компаратор: пере-рендерить узел только если
 * у НЕГО САМОГО реально поменялся статус "выбран/не выбран" — остальные
 * тысячи узлов, для которых ничего не изменилось, пропускают рендер
 * полностью (а раз не рендерятся — не пере-рендерятся и их поддеревья).
 */
const SelectableDirectoryTreeItem = memo(
    SelectableDirectoryTreeItemComponent,
    (prev, next) =>
        prev.node === next.node &&
        prev.ancestors === next.ancestors &&
        prev.onSelect === next.onSelect &&
        prev.autoOpenIds === next.autoOpenIds &&
        prev.scrollTargetId === next.scrollTargetId &&
        (prev.node.id === prev.selectedId) === (next.node.id === next.selectedId)
);

export default SelectableDirectoryTreeItem;
